// src/App.js

import React, { Suspense, useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useProductos } from './data/useProductos';
import { useCategoriasTalles } from './data/useCategoriasTalles';

const Home = React.lazy(() => import('./page/home'));
const Productos = React.lazy(() => import('./page/productos'));
const Producto = React.lazy(() => import('./page/producto'));
const Navbar = React.lazy(() => import('./componentes/Navbar'));
const Foot = React.lazy(() => import('./componentes/Foot'));
const Terminos = React.lazy(() => import('./page/Terminos'));
const ReservedPage = React.lazy(() => import('./page/ReservedPage'));
const CartPage = React.lazy(() => import('./page/Cart'));
const SuccessPage = React.lazy(() => import('./page/SuccessPage'));
const FailurePage = React.lazy(() => import('./page/FailurePage'));
const PendingPage = React.lazy(() => import('./page/PendingPage'));

const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname]);
  return null;
};



function App() {
  const location = useLocation(); 
  const [cartItems, setCartItems] = useState([]);
  const { productos, loading: loadingProductos, error: errorProductos } = useProductos();
  const { categorias, loading: loadingCategorias, error: errorCategorias } = useCategoriasTalles();

  useEffect(() => {
    const titles = {
      '/': 'Bizzi | Ropa con color, estilo y actitud',
      '/productos': 'Productos | Bizzi',
      '/cart': 'Tu carrito | Bizzi',
      '/terminos': 'Términos y condiciones | Bizzi',
      '/success': 'Compra exitosa | Bizzi',
      '/failure': 'Error en el pago | Bizzi',
      '/pending': 'Pago pendiente | Bizzi',
    };
    const title = titles[location.pathname];
    if (title) document.title = title;
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname,
        page_location: window.location.href,
      });
    }
  }, [location]);

  useEffect(() => {
    if (productos.length === 0) return;

    try {
      const storedCart = localStorage.getItem('cartItems');
      if (storedCart) {
        const initialCart = JSON.parse(storedCart);

        const validatedCart = initialCart
          .filter(item => {
            const product = productos.find(p => p.id === item.id);
            if (!product) return false;
            const sizeInfo = product.sizes.find(s => s.name === item.selectedSize);
            return !!sizeInfo && sizeInfo.stock > 0;
          })
          .map(item => {
            const product = productos.find(p => p.id === item.id);
            const sizeInfo = product.sizes.find(s => s.name === item.selectedSize);
            const availableStock = sizeInfo ? sizeInfo.stock : 0;
            const quantity = Math.min(item.quantity, availableStock);
            return {
              ...item,
              quantity,
              descuentos: product?.descuentos || [],
              price: product?.price,
              title: product?.title,
              images: product?.images,
            };
          })
          .filter(item => item.quantity > 0);

        setCartItems(validatedCart);
      }
    } catch (e) {
      console.error("Error parsing or validating cartItems:", e);
      setCartItems([]);
    }
  }, [productos]);

  useEffect(() => {
    if (productos.length > 0) {
      try {
        localStorage.setItem('cartItems', JSON.stringify(cartItems));
      } catch (e) {
        console.error("Error saving cartItems to localStorage:", e);
      }
    }
  }, [cartItems, productos]);

  const getProductInfo = (productId, selectedSizeName) => {
    const product = productos.find(p => p.id === productId);
    if (!product) return null;
    const sizeInfo = product.sizes.find(s => s.name === selectedSizeName);
    return {
      title: product.title,
      price: product.price,
      image: product.image,
      stock: sizeInfo ? sizeInfo.stock : 0
    };
  };

  // --- Funciones de gestión del carrito ---
  const handleAddToCart = (itemToAdd) => {
    setCartItems(prevItems => {
      const { id, selectedSize, quantity } = itemToAdd;
      const product = productos.find(p => p.id === id); // Busca el producto original
      const productInfo = getProductInfo(id, selectedSize);

      if (!productInfo || productInfo.stock === 0) {
        return prevItems;
      }

      const existingItemIndex = prevItems.findIndex(
        item => item.id === id && item.selectedSize === selectedSize
      );

      if (existingItemIndex > -1) {
        const updatedItems = [...prevItems];
        const currentItem = updatedItems[existingItemIndex];
        const newQuantity = Math.min(currentItem.quantity + quantity, productInfo.stock);
        
        updatedItems[existingItemIndex] = { ...currentItem, quantity: newQuantity };
        return updatedItems;
      } else {
        const initialQuantity = Math.min(quantity, productInfo.stock);
        // Incluye descuentos y cualquier otra propiedad relevante
        return [
          ...prevItems,
          {
            id,
            selectedSize,
            quantity: initialQuantity,
            descuentos: product?.descuentos || [],
            price: product?.price,
            title: product?.title,
            images: product?.images,
          }
        ];
      }
    });
  };

  const handleRemoveFromCart = (itemId, sizeToRemove) => {
    setCartItems(prevItems => prevItems.filter(item => !(item.id === itemId && item.selectedSize === sizeToRemove)));
  };

  const handleUpdateQuantity = (itemId, selectedSizeName, newQuantity) => {
    setCartItems(prevItems =>
      prevItems.map(item => {
        if (item.id === itemId && item.selectedSize === selectedSizeName) {
          const productInfo = getProductInfo(item.id, item.selectedSize);
          const availableStock = productInfo ? productInfo.stock : 0;
          
          let quantityToUpdate = parseInt(newQuantity, 10) || 1;
          quantityToUpdate = Math.max(1, Math.min(quantityToUpdate, availableStock));
          
          return { ...item, quantity: quantityToUpdate };
        }
        return item;
      }).filter(item => item.quantity > 0)
    );
  };

  const enrichedCartItems = cartItems.map(item => {
    const productInfo = getProductInfo(item.id, item.selectedSize);
    return productInfo ? { ...item, ...productInfo } : null;
  }).filter(Boolean);

  if (loadingProductos || loadingCategorias) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-offwhite">
      <img
        src={process.env.PUBLIC_URL + "/logo.png"}
        alt="Bizzi logo"
        className="w-32 h-32 mb-6"
        style={{ objectFit: "contain" }}
      />
      <p className="text-xl text-gray-700 text-center">
        Cargando productos...
      </p>
    </div>
    );
  }

  if (errorProductos || errorCategorias) {
    const errorMessage = (errorProductos || errorCategorias)?.message || 
                        String(errorProductos || errorCategorias) || 
                        'Error al cargar datos';
    return (
      <div className="flex justify-center items-center min-h-screen bg-red-100">
        <p className="text-xl text-red-700">
          {errorMessage}
        </p>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop/>
      <Suspense fallback={<div className="flex flex-col justify-center items-center min-h-screen bg-offwhite"><span className="text-xl text-gray-700">Cargando...</span></div>}>
        <Navbar
          cartItems={enrichedCartItems}
          onRemoveItem={handleRemoveFromCart}
          onUpdateQuantity={handleUpdateQuantity}
        />
        <Routes>
          <Route path="/" element={<Home productos={productos} onAddToCart={handleAddToCart} />} />
          <Route path="/productos" element={<Productos productos={productos} onAddToCart={handleAddToCart} />} />
          <Route path="/producto/:id" element={<Producto productos={productos} onAddToCart={handleAddToCart} categorias={categorias} />} />
          <Route
            path='/cart'
            element={
              <CartPage
                cartItems={enrichedCartItems}
                onRemoveItem={handleRemoveFromCart}
                onUpdateQuantity={handleUpdateQuantity}
                setCartItems={setCartItems}
              />
            }
          />
          <Route path="/success" element={<SuccessPage setCartItems={setCartItems} />} />
          <Route path="/failure" element={<FailurePage />} />
          <Route path="/pending" element={<PendingPage />} />
          <Route path="/terminos" element={<Terminos />} />
          <Route path="/reserved" element={<ReservedPage setCartItems={setCartItems} />} />
        </Routes>
        <Foot />
      </Suspense>
    </>
  );
}

export default App;